import { BrowserRouter, Routes, Route } from 'react-router';
import { lazy, Suspense } from 'react';

/**
 * types
 */
import { TariffStatusEnum } from '@libs/types';

/**
 * Store
 */
import { useAppStore } from '@store/app-store';

/**
 * UI
 */
import PageLoading from '@components/page-loading';
import ScrollToTop from '@components/scroll-to-top';

/**
 * Layouts
 */
import AccountLayout from '@layouts/account-layout';
import AuthPagesLayout from '@layouts/auth-pages-layout';
import InfoPageLayout from '@layouts/info-page-layout';
import NotificationsLayout from '@layouts/notifications-layout';
import ErrorBoundary from '@layouts/error-boundary';

/**
 * Providers
 */
import { AuthProvider } from '@providers/auth-provider';

/**
 * Pages
 */

const CardPage = lazy(() => import('@pages/lk/card-page'));
const AccountRootPage = lazy(() => import('@pages/lk/account-main'));
const CreatePush = lazy(
  () => import(/* webpackChunkName: "push-feature" */ '@pages/lk/create-push'),
);
const CustomPushPage = lazy(
  () =>
    import(
      /* webpackChunkName: "push-feature" */ '@pages/lk/create-push-custom'
    ),
);
const PushTemplatesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "push-feature" */ '@pages/lk/create-push-template'
    ),
);
const CreateFromTemplatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "push-feature" */ '@pages/lk/create-push-from-template'
    ),
);
const CreatePushConfirmPage = lazy(
  () =>
    import(
      /* webpackChunkName: "push-feature" */ '@pages/lk/create-push-confirm'
    ),
);
const PushHistoryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "push-feature" */ '@pages/lk/push-history-page'
    ),
);
const QrCodePage = lazy(() => import('@pages/lk/qr-code-page'));
const AccountPlanPage = lazy(() => import('@pages/lk/account-plan'));
const LimitsTrialPage = lazy(() => import('@pages/lk/limits-trial'));
const LimitsPage = lazy(() => import('@pages/lk/limits-page'));
const HelpPage = lazy(() => import('@pages/help-page'));
const ProfilePage = lazy(() => import('@pages/lk/profile-page'));
const ContactUsPage = lazy(() => import('@pages/lk/contact-us-page'));
const NotFound = lazy(() => import('@pages/not-found'));
const TariffsPage = lazy(() => import('@pages/tariffs'));
const TermsPage = lazy(() => import('@pages/terms'));
const PrivacyPolicyPage = lazy(() => import('@pages/privacy-policy'));
const ContactsPage = lazy(() => import('@pages/contacts'));
const FeedbackPage = lazy(() => import('@pages/feedback'));

const CardReadyPage = lazy(
  () =>
    import(/* webpackChunkName: "onboarding" */ '@pages/onbording/card-ready'),
);
const CardRewardPage = lazy(
  () =>
    import(/* webpackChunkName: "onboarding" */ '@pages/onbording/card-reward'),
);
const YourQrCodePage = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '@pages/onbording/your-qr-code'
    ),
);
const ScrapPage = lazy(
  () => import(/* webpackChunkName: "onboarding" */ '@pages/onbording/scrap'),
);
const EasyWayPage = lazy(
  () =>
    import(/* webpackChunkName: "onboarding" */ '@pages/onbording/easy-way'),
);
const PlaceYourQrPage = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '@pages/onbording/place-your-qr'
    ),
);
const WinWinPage = lazy(
  () => import(/* webpackChunkName: "onboarding" */ '@pages/onbording/win-win'),
);

const PremaidCards = lazy(
  () => import(/* webpackChunkName: "premaid" */ '@pages/premaid_cards/main'),
);

const InfoWithoutFb = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '@pages/onbording/info-without-fb'
    ),
);

// Admin pages
const AdminPage = lazy(
  () => import(/* webpackChunkName: "onboarding" */ '@pages/admin/pages/root'),
);
const AdminUserDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '@pages/admin/pages/user-details'
    ),
);
const AdminLayout = lazy(
  () => import(/* webpackChunkName: "onboarding" */ '@layouts/admin-layout'),
);
const AdminCardEditPage = lazy(
  () =>
    import(/* webpackChunkName: "onboarding" */ '@pages/admin/pages/card-edit'),
);

// auth pages
import LoginPage from '@pages/auth/login';
import SignUpPage from '@pages/auth/signup';
import ForgotPasswordPage from '@pages/auth/forgot-password';
import ResetPasswordPage from '@pages/auth/reset-password';
import ChangePasswordPage from '@pages/auth/create-new-password';
import PasswordUpdatedPage from '@pages/auth/password-updated';
import VerifyEmailPage from '@pages/auth/verify-email';
import EmailConfirmedPage from '@pages/auth/email-confirmed';

// UI
import ProtectedRoute from '@components/protected-route';
import AuthenticatedRoute from '@components/authenticated-route';
import ForNotAuthenticatedUser from '@components/for-not-authenticated-route';

function App() {
  const tariffStatus = useAppStore(
    (state) => state.userData?.user_tariff_info.user_tariff.status || null,
  );

  const hasAdditionalQuota = useAppStore(
    (state) => state.userData?.additional_quota,
  );

  const isTariffEnded = tariffStatus === TariffStatusEnum.ENDED;

  const isPushCreationAllowed = !isTariffEnded || Boolean(hasAdditionalQuota);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <ErrorBoundary>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="account" element={<AuthenticatedRoute />}>
                <Route element={<AccountLayout />}>
                  <Route index element={<AccountRootPage />} />
                  <Route path=":cardId" element={<CardPage />} />

                  <Route
                    path=":cardId/push_notification"
                    element={
                      <ProtectedRoute
                        isAllowed={isPushCreationAllowed}
                        redirectPath="/account/limits_trial"
                      >
                        <CreatePush />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path=":cardId/push_notification/create"
                    element={
                      <ProtectedRoute
                        isAllowed={isPushCreationAllowed}
                        redirectPath="/account/limits_trial"
                      >
                        <CustomPushPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path=":cardId/push_notification/template"
                    element={
                      <ProtectedRoute
                        isAllowed={isPushCreationAllowed}
                        redirectPath="/account/limits_trial"
                      >
                        <PushTemplatesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path=":cardId/push_notification/template/:templateId/create"
                    element={
                      <ProtectedRoute
                        isAllowed={isPushCreationAllowed}
                        redirectPath="/account/limits_trial"
                      >
                        <CreateFromTemplatePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path=":cardId/push_notification/confirm/:notifId"
                    element={
                      <ProtectedRoute
                        isAllowed={isPushCreationAllowed}
                        redirectPath="/account/limits_trial"
                      >
                        <CreatePushConfirmPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path=":cardId/push_notification/history"
                    element={<PushHistoryPage />}
                  />
                  <Route path="plan" element={<AccountPlanPage />} />

                  <Route path="limits_trial" element={<LimitsTrialPage />} />
                  <Route path="limits" element={<LimitsPage />} />
                  <Route path="help" element={<HelpPage />} />

                  <Route path="profile" element={<ProfilePage />} />

                  <Route path="contact-us" element={<ContactUsPage />} />
                  <Route path="tariffs" element={<TariffsPage />} />
                </Route>
                <Route path=":cardId/qr_code" element={<QrCodePage />} />
              </Route>

              <Route element={<NotificationsLayout />}>
                <Route path="card_ready" element={<CardReadyPage />} />
              </Route>
              <Route path="card_reward" element={<CardRewardPage />} />
              <Route path="your_qr_code" element={<YourQrCodePage />} />
              <Route path="easy_way" element={<EasyWayPage />} />
              <Route path="place_your_qr" element={<PlaceYourQrPage />} />
              <Route path="win_win" element={<WinWinPage />} />

              <Route element={<AuthenticatedRoute />}>
                <Route path="main" element={<ScrapPage />} />
                <Route element={<NotificationsLayout />}>
                  <Route path="info-without-fb" element={<InfoWithoutFb />} />
                </Route>
              </Route>

              <Route element={<InfoPageLayout />}>
                <Route path="terms" element={<TermsPage />} />
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="feedback" element={<FeedbackPage />} />
              </Route>

              {/* Auth pages */}
              <Route element={<ForNotAuthenticatedUser />}>
                <Route element={<AuthPagesLayout />}>
                  <Route path="login" element={<LoginPage />} />
                  <Route path="sign-up" element={<SignUpPage />} />
                  <Route
                    path="forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="reset-password-link"
                    element={<ResetPasswordPage />}
                  />
                  <Route
                    path="change-password"
                    element={<ChangePasswordPage />}
                  />
                  <Route
                    path="password-updated"
                    element={<PasswordUpdatedPage />}
                  />
                  <Route path="verify-email" element={<VerifyEmailPage />} />
                  <Route
                    path="email-confirmed"
                    element={<EmailConfirmedPage />}
                  />
                </Route>
              </Route>

              {/* Premaid routes */}
              <Route element={<AuthenticatedRoute />}>
                <Route path="premaid_card" element={<PremaidCards />} />
              </Route>

              {/* Admin routes */}
              <Route element={<AuthenticatedRoute />}>
                <Route element={<NotificationsLayout />}>
                  <Route element={<AdminLayout />}>
                    <Route path="admin" element={<AdminPage />} />

                    <Route path="admin">
                      <Route
                        path="users/:userId"
                        element={<AdminUserDetails />}
                      />
                      <Route
                        path="users/:userId/cards/:cardId/edit"
                        element={<AdminCardEditPage />}
                      />
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* Catch-all route for undefined paths */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
